import React from 'react';
import styled from '@emotion/styled';
import { tokens } from '../../../public/tokens.js';
import { Link } from 'react-router-dom';

const { colors, typography, globals } = tokens;

/**
 * DisplayText - Largest text for hero sections
 */
export const DisplayText = styled.h1`
    font-family: ${typography.Heading.display.fontFamily};
    font-weight: ${({ fontWeight }) => fontWeight || typography.Heading.display.fontWeight};
    font-size: ${typography.Heading.display.fontSize};
    line-height: ${typography.Heading.display.lineHeight};
`;

/**
 * Heading1 - Primary page titles
 * @param {string} fontWeight - Override font weight
 */
export const Heading1 = styled.h1`
    font-weight: ${({ fontWeight }) => fontWeight || typography.Heading['heading-1'].fontWeight};
    font-size: ${typography.Heading['heading-1'].fontSize};
    line-height: ${typography.Heading['heading-1'].lineHeight};
`;

/**
 * Heading2 - Major section headings
 * @param {string} fontWeight - Override font weight
 */
export const Heading2 = styled.h2`
    font-weight: ${({ fontWeight }) => fontWeight || typography.Heading['heading-2'].fontWeight};
    font-size: ${typography.Heading['heading-2'].fontSize};
    line-height: ${typography.Heading['heading-2'].lineHeight};
`;

/**
 * Heading3 - Minor section headings
 */
export const Heading3 = styled.h3`
    ${({ fontWeight, children, key, ref, as, theme, className, ...props }) => ({
        fontSize: typography.Heading['heading-3'].fontSize,
        lineHeight: typography.Heading['heading-3'].lineHeight,
        fontWeight: fontWeight ?? typography.Heading['heading-3'].fontWeight,
        ...props,
    })}
`;

/**
 * Heading4 - Small headings, inline headers
 */
export const Heading4 = styled.h4`
    font-weight: ${({ fontWeight }) => fontWeight ?? typography.Heading['heading-4'].fontWeight};
    font-size: ${typography.Heading['heading-4'].fontSize};
    line-height: ${typography.Heading['heading-4'].lineHeight};
`;

/**
 * Heading5 - Very small headings
 * @param {string} fontWeight - Override font weight
 */
export const Heading5 = styled.h5`
    font-weight: ${({ fontWeight }) => fontWeight || typography.Heading['heading-5'].fontWeight};
    font-size: ${typography.Heading['heading-5'].fontSize};
    line-height: ${typography.Heading['heading-5'].lineHeight};

    ${({ marginBottom }) => marginBottom && `margin-bottom : ${marginBottom}`}
`;

/**
 * Heading6 - Smallest heading level
 */
export const Heading6 = styled.h6`
    font-weight: ${typography.Heading['heading-6'].fontWeight};
    font-size: ${typography.Heading['heading-6'].fontSize};
    line-height: ${typography.Heading['heading-6'].lineHeight};

    ${({ marginBottom }) => marginBottom && `margin-bottom : ${marginBottom}`}
`;

/**
 * BodyText - Default paragraph text
 */
export const BodyText = styled.p`
    font-weight: ${typography['body-normal'].Regular.fontWeight};
    font-size: ${typography['body-normal'].Regular.fontSize};
    line-height: ${typography['body-normal'].Regular.lineHeight};
`;

/**
 * BodyTextBold - Emphasized paragraph text
 */
export const BodyTextBold = styled.p`
    font-weight: ${typography['body-normal'].Bold.fontWeight};
    font-size: ${typography['body-normal'].Bold.fontSize};
    line-height: ${typography['body-normal'].Bold.lineHeight};
`;

/**
 * BodySmall - Secondary/smaller body text
 */
export const BodySmall = styled.p`
    font-weight: ${({ fontWeight }) => fontWeight ?? typography['body-small'].Regular.fontWeight};
    font-size: ${({ fontSize }) => fontSize ?? typography['body-small'].Regular.fontSize};
    line-height: ${({ lineHeight }) => lineHeight ?? typography['body-small'].Regular.lineHeight};

    ${({ $marginBottom }) => $marginBottom && `margin-bottom: ${$marginBottom};`}
`;

/**
 * LabelText - Form field labels and metadata
 * @param {string} as - Render as different element (span, p, div)
 */
export const LabelText = styled.label`
    font-weight: ${({ fontWeight }) => fontWeight ?? typography['label-normal'].Regular.fontWeight};
    font-size: ${typography['label-normal'].Regular.fontSize};
    line-height: ${typography['label-normal'].Regular.lineHeight};
`;

/**
 * LabelSmall - Small labels and captions
 * @param {string} as - Render as different element
 */
export const LabelSmall = styled.label`
    font-weight: ${typography['label-small'].Regular.fontWeight};
    font-size: ${typography['label-small'].Regular.fontSize};
    line-height: ${typography['label-small'].Regular.lineHeight};
`;

/**
 * LabelMedium - Medium size labels
 * @param {string} as - Render as different element
 */
export const LabelMedium = styled.label`
    font-size: ${({ fontSize }) => fontSize ?? `${tokens.typography['body-small'].Regular.fontSize}`};
    line-height: ${typography['body-small'].Regular.lineHeight};
    color: ${({ color }) => (color ? color : tokens.colors.text.primary)};
`;

/**
 * XSmallText - Extra small text (8px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const XSmallText = styled.span`
    font-size: ${typography.fontSize['xs-8px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['100']};
`;

/**
 * SmallText - Small text (10px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const SmallText = styled.span`
    font-size: ${typography.fontSize['sm-10px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.semibold};
    line-height: ${typography.lineHeights['100']};
`;

/**
 * MediumText - Medium text (12px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const MediumText = styled.span`
    font-size: ${typography.fontSize['md-12px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['100']};
`;

/**
 * BaseText - Base text (14px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const BaseText = styled.span`
    font-size: ${typography.fontSize['lg-14px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['110']};
`;

/**
 * LargeText - Large text (16px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const LargeText = styled.span`
    font-size: ${typography.fontSize['base-16px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['110']};
`;

/**
 * XLargeText - Extra large text (18px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const XLargeText = styled.span`
    font-size: ${typography.fontSize['xl-18px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['130']};
`;

/**
 * XXLargeText - 2X large text (20px)
 * @param {string} as - Render as different element
 * @param {string} fontWeight - Font weight override
 */
export const XXLargeText = styled.span`
    font-size: ${typography.fontSize['2xl-20px']};
    font-weight: ${({ fontWeight }) => fontWeight || typography.fontWeights.regular};
    line-height: ${typography.lineHeights['130']};
`;

/**
 * LabelSmallBold - Important small labels
 * @param {string} as - Render as different element
 */
export const LabelSmallBold = styled.label`
    font-family: ${typography['label-small'].Bold.fontFamily};
    font-weight: ${typography['label-small'].Bold.fontWeight};
    font-size: ${typography['label-small'].Bold.fontSize};
    line-height: ${typography['label-small'].Bold.lineHeight};
`;

/**
 * ErrorMessage - Display error text
 */
export const ErrorMessage = styled.div`
    color: ${colors.states.alert.default};
    font-size: ${({ fontSize }) => fontSize ?? typography.fontSize['md-12px']};
    margin-top: 4px;
`;

/**
 * TextLink - Link text
 * @param {string} color - Custom link color
 * @param {boolean} bold - Make text bold
 */
export const TextLink = styled.a`
  color: ${({ color }) => color || colors.text.link};
  text-decoration: none;
  font-weight: ${({ bold }) => (bold ? typography.fontWeights.bold : 'inherit')};
  cursor: pointer;

  &:hover {
    color: ${({ hoverColor }) => hoverColor || colors.brand.blue[400]};
    text-decoration: 'underline';
  }
  &:active {
    color: ${({ activeColor }) => activeColor || colors.brand.blue[400]};
    text-decoration: 'underline;
  }
`;

export const TextLink2 = styled(Link)`
    color: ${({ color }) => color || colors.text.link};
    text-decoration: none;
    font-weight: ${({ bold }) => (bold ? typography.fontWeights.bold : 'inherit')};
    cursor: pointer;

    &:hover {
        color: ${({ hoverColor }) => hoverColor || colors.brand.blue[400]};
    }
`;

export const StandardErrorMessage = ({ formik, name }) => {
    return formik.touched[name] && formik.errors[name] ? <ErrorMessage>{formik.errors[name]}</ErrorMessage> : null;
};
